import { Droppable } from '@hello-pangea/dnd'
import KanbanCard from './KanbanCard'

const KanbanColumn = ({
  column,
  gyms,
  userRole,
  user,
  isDropDisabled = false,
  onViewDetails,
  onLogVisit,
  onUploadDocuments,
  onPreRecce,
  onRemoveGym,
  onInternalApprove,
  onInternalReject
}) => {
  const getColumnColor = (stage) => {
    const colors = {
      'New Lead': 'border-gray-300',
      'Site Visit': 'border-blue-300',
      'Internal Approval': 'border-amber-300',
      'CEO Approval': 'border-pink-400',
      'Proposal Sent': 'border-yellow-300',
      'Agreement Signed': 'border-green-300',
      'Facility Work': 'border-purple-300',
      'Post Reki Approved': 'border-indigo-300',
      'Gym Onboard': 'border-red-300'
    }
    return colors[stage] || 'border-gray-300'
  }

  const canUserDragToColumn = (stage) => {
    // BD Executive (level 4) can only drop gyms up to Site Visit
    if (userRole?.level === 4) {
      const allowedDropStages = ['New Lead', 'Site Visit']
      return allowedDropStages.includes(stage)
    }
    
    // Head of Acquisition (level 3) can drop gyms up to Facility Work
    // Also allow Gym Onboard for member data upload (they can only upload, not move the gym)
    if (userRole?.level === 3) {
      const allowedDropStages = ['Site Visit', 'Internal Approval', 'CEO Approval', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Gym Onboard']
      return allowedDropStages.includes(stage)
    }
    
    return true // Other roles can move to any stage based on their permissions
  }

  const canDragGym = (gym) => {
    // BD Executive can only drag gyms they created or are assigned to
    if (userRole?.level === 4) {
      return gym.assignedUserId === user?.id
    }
    return true
  }

  const columnDroppable = canUserDragToColumn(column.id)
  const isHeadOfAcquisition = userRole?.level === 3
  const isBDExecutive = userRole?.level === 4
  const isRestricted = !columnDroppable && (isBDExecutive || isHeadOfAcquisition)

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200">
      <div className={`flex-shrink-0 p-4 border-t-4 ${getColumnColor(column.id)} ${
        isRestricted ? 'opacity-60 bg-gray-50' : ''
      }`}>
        <div className="flex justify-between items-center">
          <h3 className={`font-medium ${
            isRestricted ? 'text-gray-500' : 'text-gray-900'
          }`}>
            {column.title}
          </h3>
          <div className="flex items-center space-x-2">
            <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
              {gyms.length}
            </span>
            {isRestricted && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-red-500">🔒</span>
                <span className="text-xs text-red-500 font-medium">
                  {isBDExecutive ? 'BD Restricted' : 'HoA Restricted'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <Droppable 
        droppableId={String(column.id)}
        isDropDisabled={isDropDisabled || !columnDroppable}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 overflow-y-auto p-4 kanban-column-scroll ${
              snapshot.isDraggingOver 
                ? columnDroppable
                  ? 'bg-blue-50 ring-2 ring-blue-200'
                  : 'bg-red-50 ring-2 ring-red-200 border-2 border-dashed border-red-300' // Red border for invalid drop
                : isRestricted
                  ? 'bg-gray-50 border border-dashed border-gray-300' // Dashed border for restricted columns
                  : ''
            } transition-colors duration-200 rounded-b-lg`}
            style={{ minHeight: '500px', maxHeight: 'calc(100vh - 300px)' }}
          >
            <div className="space-y-3">
              {gyms.map((gym, index) => (
                <KanbanCard
                  key={gym.id}
                  gym={gym}
                  index={index}
                  canDrag={canDragGym(gym)}
                  onViewDetails={onViewDetails}
                  onLogVisit={onLogVisit}
                  onUploadDocuments={onUploadDocuments}
                  onPreRecce={onPreRecce}
                  onRemoveGym={onRemoveGym}
                  onInternalApprove={onInternalApprove}
                  onInternalReject={onInternalReject}
                  userRole={userRole}
                />
              ))}
              {provided.placeholder}
              
              {/* Empty state */}
              {gyms.length === 0 && (
                <div className="flex items-center justify-center h-32 text-gray-400">
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
                    </svg>
                    <p className="text-sm">No gyms in {column.title.toLowerCase()}</p>
                    <p className="text-xs text-gray-300 mt-1">Drop gyms here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Droppable>
    </div>
  )
}

export default KanbanColumn 
